/**
 * Cor da pintura e código do fabricante da OS (F3.6 dos módulos verticais,
 * pedida na avaliação de dano da funilaria).
 *
 * A cor é o nome que o funileiro fala ("Prata Metálico"); o código é o da
 * etiqueta do fabricante ("1F7", "LA7W") — é ele que a loja de tinta usa para
 * achar a fórmula, e errar a tinta é o retrabalho mais caro do segmento.
 *
 * Texto livre nos dois: não existe lista fechada de cor nem de código, e cada
 * montadora tem o seu formato. O que a normalização garante é que o mesmo
 * código digitado de dois jeitos (" la7w ", "LA7W") seja o mesmo texto, e que
 * nada passe do tamanho da coluna (`service_orders.paint_color/paint_code`).
 */
export const PAINT_COLOR_MAX = 60;
export const PAINT_CODE_MAX = 20;

const collapse = (value: string) => value.replace(/\s+/g, ' ').trim();

/** Nome da cor: espaços colapsados, até o tamanho da coluna. Vazio → `null`. */
export function normalizePaintColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = collapse(value).slice(0, PAINT_COLOR_MAX).trim();
  return text.length > 0 ? text : null;
}

/** Código do fabricante: em maiúsculas (é assim na etiqueta), sem espaço sobrando. Vazio → `null`. */
export function normalizePaintCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = collapse(value).toUpperCase().slice(0, PAINT_CODE_MAX).trim();
  return text.length > 0 ? text : null;
}

/**
 * Fórmula de mistura da tinta (F3.6) — a receita que o sistema da loja de
 * tinta da oficina dá para o código ("variante 2, 3 camadas…"). Texto livre,
 * numa linha, até o tamanho da coluna (`service_orders.paint_formula`).
 *
 * É da OFICINA, não do carro: outra oficina, com outro fornecedor, teria outra
 * fórmula para o mesmo código. Por isso fica só na OS, e a próxima OS do mesmo
 * carro nesta oficina a mostra como referência da última vez.
 */
export const PAINT_FORMULA_MAX = 200;

export function normalizePaintFormula(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = collapse(value).slice(0, PAINT_FORMULA_MAX).trim();
  return text.length > 0 ? text : null;
}

/** "Prata Metálico · 1F7", ou só um dos dois; `null` quando não há nenhum. */
export function formatPaint(color: string | null | undefined, code: string | null | undefined): string | null {
  const parts = [color?.trim(), code?.trim()].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Cor do VEÍCULO — a amostra da etapa 2 da abertura (Branco, Prata, Preto…),
 * para qualquer tipo de OS. É a cor de cadastro (`vehicles.color`), não a da
 * pintura da funilaria acima: "Prata" aqui, "Prata Metálico · 1F7" lá.
 *
 * "Outro" é a amostra de "nenhuma das anteriores", não uma cor: vira `null`,
 * senão o cadastro do carro passaria a dizer que a cor dele é "Outro".
 */
export const VEHICLE_COLOR_MAX = 50;

export function normalizeVehicleColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = collapse(value).slice(0, VEHICLE_COLOR_MAX).trim();
  if (text.length === 0 || text.toLowerCase() === 'outro') return null;
  return text;
}
